import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, RefreshCw, X, Check, Search, Wifi } from 'lucide-react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import Badge from '../components/Badge';
import Modal from '../components/Modal';
import { ErrMsg } from '../components/FormField';
import { PageHeader } from '../components/FilterBar';
import SearchableSelect from '../components/SearchableSelect';

const TYPE_LABELS = {
  mobile_brand: 'Mobile Brands',
  laptop_brand: 'Laptop Brands',
  printer_brand: 'Printer Brands',
  vendor: 'Vendors',
  os_version: 'OS / Windows Versions',
  office_version: 'Office Versions',
  toner_model: 'Toner Models',
  accessory_category: 'Accessory Categories',
  system_type: 'ERP / System Types',
  damage_type: 'Damage Types',
  repair_vendor: 'Repair Vendors',
};

const TYPE_ICONS = {
  mobile_brand: '📱', laptop_brand: '💻', printer_brand: '🖨️',
  vendor: '🏢', os_version: '🪟', office_version: '📄',
  toner_model: '🖤', accessory_category: '📦', system_type: '🔐',
  damage_type: '⚠️', repair_vendor: '🔧',
};

// Inline editable row — defined at module level
const EditRow = React.memo(({ item, onSave, onDelete, isAdmin, isIT }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(item.value);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!val.trim()) return;
    setSaving(true);
    try {
      await onSave(item.id, { value: val, sort_order: item.sort_order, is_active: item.is_active });
      setEditing(false);
    } finally { setSaving(false); }
  };

  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg group transition-colors"
      style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}
      onMouseEnter={e => e.currentTarget.style.background = 'rgba(59,130,246,0.05)'}
      onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}>
      {editing ? (
        <>
          <input value={val} onChange={e => setVal(e.target.value)} className="input-dark flex-1" style={{ fontSize: 13 }}
            onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setEditing(false); setVal(item.value); } }}
            autoFocus/>
          <button onClick={save} disabled={saving} className="btn-ghost px-2 py-1 text-xs" style={{ color: '#10b981' }}>
            {saving ? '...' : <Check size={12}/>}
          </button>
          <button onClick={() => { setEditing(false); setVal(item.value); }} className="btn-ghost px-2 py-1 text-xs" style={{ color: '#ef4444' }}>
            <X size={12}/>
          </button>
        </>
      ) : (
        <>
          <span className="flex-1 text-sm" style={{ color: item.is_active ? '#e2e8f0' : '#334155', textDecoration: item.is_active ? 'none' : 'line-through' }}>
            {item.value}
          </span>
          {!item.is_active && <span className="text-xs" style={{ color: '#334155' }}>inactive</span>}
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {isIT && <button onClick={() => setEditing(true)} className="btn-ghost px-1.5 py-1"><Edit2 size={11}/></button>}
            {isAdmin && <button onClick={() => onDelete(item.id)} className="btn-ghost px-1.5 py-1" style={{ color: '#ef4444' }}><Trash2 size={11}/></button>}
          </div>
        </>
      )}
    </div>
  );
});

export default function MasterFiles() {
  const { isAdmin, isIT } = useAuth();
  const [mainTab, setMainTab] = useState('dropdowns'); // 'dropdowns' | 'packages'
  const [allData, setAllData] = useState([]);
  const [types, setTypes] = useState([]);
  const [activeType, setActiveType] = useState('mobile_brand');
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);
  const [newValue, setNewValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // ISP Package Master state
  const [packages, setPackages] = useState([]);
  const [isps, setIsps] = useState([]);
  const [pkgModal, setPkgModal] = useState(null);
  const [pkgSelected, setPkgSelected] = useState(null);
  const [pkgForm, setPkgForm] = useState({});
  const [pkgSaving, setPkgSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [data, typeList] = await Promise.all([
        api.get('/master-files').then(r => r.data),
        api.get('/master-files/types').then(r => r.data)
      ]);
      setAllData(data);
      setTypes(typeList);
      // Set first type as active if needed
      if (typeList.length && !typeList.find(t => t.type === activeType)) {
        setActiveType(typeList[0].type);
      }
    } catch {} finally { setLoading(false); }
  }, [activeType]);

  useEffect(() => { load(); }, []);

  const loadPackages = useCallback(async () => {
    try {
      const [p, i] = await Promise.all([
        api.get('/master-files/isp-packages').then(r => r.data),
        api.get('/connections/isps').then(r => r.data)
      ]);
      setPackages(p); setIsps(i);
    } catch {}
  }, []);

  useEffect(() => { if (mainTab === 'packages') loadPackages(); }, [mainTab, loadPackages]);

  const handleSavePkg = async e => {
    e.preventDefault(); setPkgSaving(true);
    try {
      pkgSelected
        ? await api.put(`/master-files/isp-packages/${pkgSelected.id}`, pkgForm)
        : await api.post('/master-files/isp-packages', pkgForm);
      setPkgModal(null); setPkgSelected(null); loadPackages();
    } catch {} finally { setPkgSaving(false); }
  };

  const filteredItems = allData
    .filter(d => d.type === activeType)
    .filter(d => !search || d.value.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => (a.sort_order - b.sort_order) || a.value.localeCompare(b.value));

  const handleAdd = async e => {
    e.preventDefault();
    if (!newValue.trim()) return;
    setSaving(true); setError('');
    try {
      await api.post('/master-files', { type: activeType, value: newValue.trim(), sort_order: filteredItems.length + 1 });
      setNewValue(''); setAdding(false);
      load();
    } catch (err) { setError(err.response?.data?.error || 'Failed'); } finally { setSaving(false); }
  };

  const handleSave = async (id, updates) => {
    await api.put(`/master-files/${id}`, updates);
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this value? It will no longer appear in dropdowns.')) return;
    await api.delete(`/master-files/${id}`);
    load();
  };

  const currentType = types.find(t => t.type === activeType);

  return (
    <div className="space-y-4">
      <PageHeader title="Master Files">
        <button onClick={load} className="btn-ghost p-2"><RefreshCw size={13} className={loading ? 'animate-spin' : ''}/></button>
      </PageHeader>

      {/* Main tab switcher */}
      <div className="flex gap-1 p-1 rounded-xl w-fit" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
        {[['dropdowns','Dropdown Values'],['packages','ISP Packages (Dongle/Voice)']].map(([key,label]) => (
          <button key={key} onClick={() => setMainTab(key)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all"
            style={{ background: mainTab===key?'rgba(59,130,246,0.2)':'transparent', color: mainTab===key?'#60a5fa':'#475569', border: mainTab===key?'1px solid rgba(59,130,246,0.3)':'1px solid transparent' }}>
            {key==='packages'&&<Wifi size={12}/>}{label}
          </button>
        ))}
      </div>

      {mainTab === 'packages' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs" style={{ color: '#334155' }}>Manage ISP packages for Dongles and Voice Numbers. These appear in the Package dropdown in both modules.</p>
            {isIT && <button onClick={() => { setPkgSelected(null); setPkgForm({ type: 'data' }); setPkgModal('form'); }} className="btn-primary text-xs"><Plus size={12}/> Add Package</button>}
          </div>
          <div className="glass glow-border rounded-2xl overflow-hidden">
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                {['ISP','Package Name','Type','Data GB','Cost (LKR/mo)','Tax %','Notes',''].map(h => (
                  <th key={h} style={{ padding: '9px 12px', textAlign: 'left', fontSize: 10, color: '#334155', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {packages.map((p, i) => (
                  <tr key={p.id||i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
                    onMouseEnter={e=>e.currentTarget.style.background='rgba(59,130,246,0.05)'}
                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    <td style={{ padding: '9px 12px', color: '#60a5fa', fontWeight: 500 }}>{p.isp_name}</td>
                    <td style={{ padding: '9px 12px', color: '#e2e8f0', fontWeight: 500 }}>{p.name}</td>
                    <td style={{ padding: '9px 12px' }}><span className="text-xs px-2 py-0.5 rounded-full" style={{ background: p.type==='voice'?'rgba(52,211,153,0.15)':'rgba(59,130,246,0.15)', color: p.type==='voice'?'#34d399':'#60a5fa' }}>{p.type}</span></td>
                    <td style={{ padding: '9px 12px', color: '#94a3b8' }}>
                      {p.data_anytime_gb ? `${p.data_anytime_gb}GB` : p.data_peak_gb ? `${p.data_peak_gb}GB peak` : '—'}
                    </td>
                    <td style={{ padding: '9px 12px', color: '#34d399', fontWeight: 600 }}>{p.monthly_cost ? Number(p.monthly_cost).toLocaleString() : '—'}</td>
                    <td style={{ padding: '9px 12px', color: '#475569' }}>{p.tax_rate || 0}%</td>
                    <td style={{ padding: '9px 12px', color: '#334155', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.notes || '—'}</td>
                    <td style={{ padding: '9px 12px' }}>
                      {isIT && <button onClick={() => { setPkgSelected(p); setPkgForm({...p}); setPkgModal('form'); }} className="btn-ghost px-2 py-1 text-xs"><Edit2 size={11}/></button>}
                      {isAdmin && <button onClick={async () => { if(window.confirm('Deactivate?')) { await api.delete(`/master-files/isp-packages/${p.id}`); loadPackages(); }}} className="btn-ghost px-2 py-1 text-xs" style={{ color: '#ef4444' }}><Trash2 size={11}/></button>}
                    </td>
                  </tr>
                ))}
                {packages.length === 0 && <tr><td colSpan={8} style={{ padding: '24px', textAlign: 'center', color: '#1e3a5f' }}>No packages yet. Add your first ISP package above.</td></tr>}
              </tbody>
            </table>
          </div>

          {pkgModal === 'form' && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(4,8,16,0.85)', backdropFilter: 'blur(8px)' }}>
              <div className="w-full max-w-lg rounded-2xl overflow-hidden" style={{ background: 'linear-gradient(135deg,#0d1528,#0a1020)', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 24px 80px rgba(0,0,0,0.8)', animation: 'slideUp 0.3s ease-out' }}>
                <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                  <span className="font-semibold text-sm" style={{ color: '#e2e8f0' }}>{pkgSelected ? 'Edit Package' : 'Add ISP Package'}</span>
                  <button onClick={() => setPkgModal(null)} className="btn-ghost p-1.5"><X size={15}/></button>
                </div>
                <form onSubmit={handleSavePkg} className="p-6 space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="block text-xs font-medium mb-1" style={{ color: '#475569' }}>ISP *</label>
                      <SearchableSelect required value={pkgForm.isp_id||''}
                        onChange={v => setPkgForm({...pkgForm, isp_id: v})}
                        options={isps.map(i => ({ value: i.id, label: i.name }))} placeholder="Select ISP"/>
                    </div>
                    <div><label className="block text-xs font-medium mb-1" style={{ color: '#475569' }}>Package Name *</label>
                      <input required value={pkgForm.name||''} onChange={e=>setPkgForm({...pkgForm,name:e.target.value})} className="input-dark" placeholder="e.g. Aviator PRO Max"/>
                    </div>
                    <div><label className="block text-xs font-medium mb-1" style={{ color: '#475569' }}>Type</label>
                      <select value={pkgForm.type||'data'} onChange={e=>setPkgForm({...pkgForm,type:e.target.value})} className="input-dark">
                        <option value="data">Data (Dongle)</option>
                        <option value="voice">Voice</option>
                        <option value="combo">Combo (Data+Voice)</option>
                      </select>
                    </div>
                    <div><label className="block text-xs font-medium mb-1" style={{ color: '#475569' }}>Monthly Cost (LKR)</label>
                      <input type="number" value={pkgForm.monthly_cost||''} onChange={e=>setPkgForm({...pkgForm,monthly_cost:e.target.value})} className="input-dark" placeholder="e.g. 2999"/>
                    </div>
                    <div><label className="block text-xs font-medium mb-1" style={{ color: '#475569' }}>Data Anytime (GB)</label>
                      <input type="number" value={pkgForm.data_anytime_gb||''} onChange={e=>setPkgForm({...pkgForm,data_anytime_gb:e.target.value})} className="input-dark" placeholder="e.g. 90"/>
                    </div>
                    <div><label className="block text-xs font-medium mb-1" style={{ color: '#475569' }}>Tax Rate (%)</label>
                      <input type="number" value={pkgForm.tax_rate||0} onChange={e=>setPkgForm({...pkgForm,tax_rate:e.target.value})} className="input-dark" placeholder="e.g. 18"/>
                    </div>
                    <div><label className="block text-xs font-medium mb-1" style={{ color: '#475569' }}>Voice Minutes</label>
                      <input type="number" value={pkgForm.outgoing_call_limit_min||''} onChange={e=>setPkgForm({...pkgForm,outgoing_call_limit_min:e.target.value})} className="input-dark" placeholder="e.g. 500 (blank = unlimited)"/>
                    </div>
                  </div>
                  <div><label className="block text-xs font-medium mb-1" style={{ color: '#475569' }}>Notes / Features</label>
                    <textarea value={pkgForm.notes||''} onChange={e=>setPkgForm({...pkgForm,notes:e.target.value})} rows={2} className="input-dark" placeholder="e.g. Unlimited WhatsApp, 90GB data"/>
                  </div>
                  <div className="flex gap-3 pt-1">
                    <button type="button" onClick={() => setPkgModal(null)} className="btn-ghost flex-1 justify-center">Cancel</button>
                    <button type="submit" disabled={pkgSaving} className="btn-primary flex-1 justify-center">{pkgSaving?'Saving...':'Save Package'}</button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {mainTab === 'dropdowns' && <div className="flex gap-4">
        {/* Type sidebar */}
        <div className="w-52 flex-shrink-0 space-y-0.5">
          {types.map(t => (
            <button key={t.type} onClick={() => { setActiveType(t.type); setSearch(''); setAdding(false); }}
              className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm text-left transition-all"
              style={{
                background: activeType === t.type ? 'rgba(59,130,246,0.2)' : 'rgba(255,255,255,0.03)',
                color: activeType === t.type ? '#60a5fa' : '#475569',
                border: activeType === t.type ? '1px solid rgba(59,130,246,0.3)' : '1px solid transparent'
              }}>
              <span className="text-base">{TYPE_ICONS[t.type] || '📋'}</span>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{TYPE_LABELS[t.type] || t.type}</div>
                <div className="text-xs" style={{ color: '#334155' }}>{t.count} values</div>
              </div>
            </button>
          ))}

          {/* Add custom type */}
          {isIT && (
            <div className="pt-2">
              <CustomTypeAdder onAdded={load}/>
            </div>
          )}
        </div>

        {/* Values panel */}
        <div className="flex-1 glass glow-border rounded-2xl p-5 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-bold text-sm" style={{ color: '#e2e8f0' }}>
                {TYPE_ICONS[activeType]} {TYPE_LABELS[activeType] || activeType}
              </h2>
              <div className="text-xs mt-0.5" style={{ color: '#334155' }}>{filteredItems.length} values</div>
            </div>
            {isIT && (
              <button onClick={() => { setAdding(true); setNewValue(''); setError(''); }}
                className="btn-primary text-xs"><Plus size={12}/> Add Value</button>
            )}
          </div>

          {/* Search */}
          <div className="relative">
            <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#334155' }}/>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search values..."
              className="input-dark pl-9 text-sm" style={{ fontSize: 12 }}/>
          </div>

          {/* Add form */}
          {adding && (
            <form onSubmit={handleAdd} className="flex gap-2">
              <input value={newValue} onChange={e => setNewValue(e.target.value)} autoFocus
                placeholder={`New ${TYPE_LABELS[activeType] || activeType} value...`}
                className="input-dark flex-1" style={{ fontSize: 13 }}/>
              <button type="submit" disabled={saving} className="btn-primary text-xs px-4">{saving ? '...' : 'Add'}</button>
              <button type="button" onClick={() => setAdding(false)} className="btn-ghost text-xs"><X size={13}/></button>
            </form>
          )}
          <ErrMsg msg={error}/>

          {/* Values list */}
          {loading ? (
            <div className="py-6 text-center" style={{ color: '#334155' }}>Loading...</div>
          ) : filteredItems.length === 0 ? (
            <div className="py-6 text-center text-sm" style={{ color: '#1e3a5f' }}>
              No values yet. Click "Add Value" to add the first one.
            </div>
          ) : (
            <div className="space-y-1">
              {filteredItems.map(item => (
                <EditRow key={item.id} item={item} onSave={handleSave} onDelete={handleDelete} isAdmin={isAdmin} isIT={isIT}/>
              ))}
            </div>
          )}
        </div>
      </div>}
    </div>
  );
}

// Add a completely new type
const CustomTypeAdder = ({ onAdded }) => {
  const [open, setOpen] = useState(false);
  const [typeName, setTypeName] = useState('');
  const [saving, setSaving] = useState(false);

  const add = async e => {
    e.preventDefault();
    if (!typeName.trim()) return;
    setSaving(true);
    try {
      await api.post('/master-files', { type: typeName.toLowerCase().replace(/\s+/g, '_'), value: 'Default Value', sort_order: 1 });
      setTypeName(''); setOpen(false); onAdded();
    } finally { setSaving(false); }
  };

  if (!open) return (
    <button onClick={() => setOpen(true)} className="w-full text-xs px-3 py-2 rounded-xl transition-colors text-left flex items-center gap-1.5" style={{ color: '#334155', border: '1px dashed rgba(255,255,255,0.06)' }}
      onMouseEnter={e => e.currentTarget.style.color='#475569'} onMouseLeave={e => e.currentTarget.style.color='#334155'}>
      <Plus size={10}/> Add new category
    </button>
  );
  return (
    <form onSubmit={add} className="p-2 rounded-xl space-y-1" style={{ background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.15)' }}>
      <input value={typeName} onChange={e => setTypeName(e.target.value)} autoFocus placeholder="e.g. Insurance Type" className="input-dark w-full" style={{ fontSize: 11 }}/>
      <div className="flex gap-1">
        <button type="submit" disabled={saving} className="btn-primary text-xs flex-1 justify-center">{saving ? '...' : 'Add'}</button>
        <button type="button" onClick={() => setOpen(false)} className="btn-ghost text-xs"><X size={11}/></button>
      </div>
    </form>
  );
};
